import { useEffect, useState, useCallback, useRef } from "react";
import config from "../config/environment";

// ---- Facebook SDK type declarations ----

interface FBLoginResponse {
  authResponse?: {
    code: string;
    accessToken?: string;
    userID?: string;
  };
  status: "connected" | "not_authorized" | "unknown";
}

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: {
      init: (params: {
        appId: string;
        cookie: boolean;
        xfbml: boolean;
        version: string;
      }) => void;
      login: (
        callback: (response: FBLoginResponse) => void,
        options: Record<string, unknown>
      ) => void;
    };
  }
}

// ---- Session info from Meta Embedded Signup postMessage ----

interface EmbeddedSignupSessionInfo {
  phone_number_id: string;
  waba_id: string;
}

export interface EmbeddedSignupResult {
  code: string;
  phone_number_id: string;
  waba_id: string;
}

// ---- Hook ----

export const useFacebookSDK = () => {
  const [sdkReady, setSdkReady] = useState(false);
  const sessionInfoRef = useRef<EmbeddedSignupSessionInfo | null>(null);

  // Listen for WA_EMBEDDED_SIGNUP postMessage from Meta popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }

      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          sessionInfoRef.current = {
            phone_number_id: data.data?.phone_number_id || "",
            waba_id: data.data?.waba_id || "",
          };
        }
      } catch {
        // Not a JSON message from Meta, ignore
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Load Facebook JS SDK
  useEffect(() => {
    if (window.FB) {
      setSdkReady(true);
      return;
    }

    window.fbAsyncInit = () => {
      window.FB.init({
        appId: config.metaAppId,
        cookie: true,
        xfbml: true,
        version: "v21.0",
      });
      setSdkReady(true);
    };

    // Only load if not already in DOM
    if (!document.getElementById("facebook-jssdk")) {
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);
    }
  }, []);

  const launchEmbeddedSignup =
    useCallback((): Promise<EmbeddedSignupResult> => {
      return new Promise((resolve, reject) => {
        if (!window.FB) {
          reject(new Error("Facebook SDK not loaded"));
          return;
        }

        // Reset sessionInfo before starting
        sessionInfoRef.current = null;

        window.FB.login(
          (response: FBLoginResponse) => {
            if (response.authResponse?.code) {
              const code = response.authResponse.code;

              // sessionInfo should have arrived via postMessage by now
              // Add a small retry window in case it arrives slightly after
              const checkSessionInfo = (retries: number) => {
                if (sessionInfoRef.current) {
                  resolve({
                    code,
                    phone_number_id: sessionInfoRef.current.phone_number_id,
                    waba_id: sessionInfoRef.current.waba_id,
                  });
                } else if (retries > 0) {
                  setTimeout(() => checkSessionInfo(retries - 1), 200);
                } else {
                  // If sessionInfo never arrived, reject with a clear message
                  reject(
                    new Error(
                      "Session info (phone_number_id, waba_id) not received from Meta"
                    )
                  );
                }
              };

              checkSessionInfo(10); // Try up to 10 times (2 seconds total)
            } else {
              reject(new Error("User cancelled or login failed"));
            }
          },
          {
            config_id: config.metaConfigId,
            response_type: "code",
            override_default_response_type: true,
            extras: {
              feature: "whatsapp_embedded_signup",
              sessionInfoVersion: 3,
            },
          }
        );
      });
    }, []);

  return { sdkReady, launchEmbeddedSignup };
};
